'use client';

import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useTranslations } from 'next-intl';
import { useEffect, useMemo, useRef, type ReactNode } from 'react';
import { getFormattedVenueOpeningHours } from '@/entities/venue/lib/get-formatted-venue-opening-hours.util';
import {
    computeVenueAvailabilitySnapshot,
    type OutageWithTimeAvailability,
} from '@/entities/venue/lib/venue-availability.utils';
import {
    VenueAvailabilityStoreContext,
    createVenueAvailabilityStore,
    type VenueAvailabilityStoreApi,
} from '@/entities/venue/model/venue-availability.store';
import { QueryApi } from '@/features/query/api';

const EMPTY_OUTAGES: OutageWithTimeAvailability[] = [];

type Props = {
    children?: ReactNode;
};

export const VenueAvailabilityProvider = ({ children }: Props) => {
    const t = useTranslations();
    const storeRef = useRef<VenueAvailabilityStoreApi | null>(null);

    const outagesQuery = useQuery(QueryApi.Outages.queries.Outages);
    const openingHoursQuery = useQuery(QueryApi.Venue.queries.OpeningHours);

    const queriesReady = outagesQuery.isFetched && openingHoursQuery.isFetched;

    const formattedOpeningHours = useMemo(
        () => getFormattedVenueOpeningHours(openingHoursQuery.data ?? [], t),
        [openingHoursQuery.data, t],
    );

    const outages = (outagesQuery.data as OutageWithTimeAvailability[] | undefined) ?? EMPTY_OUTAGES;

    const computedAtMs = queriesReady
        ? Math.max(openingHoursQuery.dataUpdatedAt, outagesQuery.dataUpdatedAt, 1)
        : dayjs().valueOf();

    if (!storeRef.current) {
        if (queriesReady) {
            const snap = computeVenueAvailabilitySnapshot({
                computedAtMs,
                outages,
                formattedOpeningHours,
            });
            storeRef.current = createVenueAvailabilityStore({
                ...snap,
                hasComputed: true,
            });
        } else {
            storeRef.current = createVenueAvailabilityStore();
        }
    }

    const store = storeRef.current;

    useVenueAvailabilityClockEffect(store, {
        queriesReady,
        outages,
        formattedOpeningHours,
        outagesDataUpdatedAt: outagesQuery.dataUpdatedAt,
        openingHoursDataUpdatedAt: openingHoursQuery.dataUpdatedAt,
    });

    return (
        <VenueAvailabilityStoreContext.Provider value={store}>
            {children ?? null}
        </VenueAvailabilityStoreContext.Provider>
    );
};

/** Recompute on query changes + schedule the next tick. */
function useVenueAvailabilityClockEffect(
    store: VenueAvailabilityStoreApi,
    {
        queriesReady,
        outages,
        formattedOpeningHours,
        outagesDataUpdatedAt,
        openingHoursDataUpdatedAt,
    }: {
        queriesReady: boolean;
        outages: OutageWithTimeAvailability[];
        formattedOpeningHours: ReturnType<typeof getFormattedVenueOpeningHours>;
        outagesDataUpdatedAt: number;
        openingHoursDataUpdatedAt: number;
    },
) {
    useEffect(() => {
        if (!queriesReady) {
            return;
        }

        let timeoutId: ReturnType<typeof setTimeout> | undefined;

        const run = () => {
            store.getState().recompute({
                computedAtMs: dayjs().valueOf(),
                outages,
                formattedOpeningHours,
            });
        };

        const schedule = () => {
            if (timeoutId !== undefined) {
                clearTimeout(timeoutId);
                timeoutId = undefined;
            }
            const nextRecomputeAtMs = store.getState().nextRecomputeAtMs;
            if (nextRecomputeAtMs == null) {
                return;
            }
            const delay = nextRecomputeAtMs - dayjs().valueOf();
            if (delay <= 0) {
                run();
                const next = store.getState().nextRecomputeAtMs;
                if (next == null) {
                    return;
                }
                const nextDelay = next - dayjs().valueOf();
                if (nextDelay > 0) {
                    timeoutId = setTimeout(run, nextDelay);
                }
                return;
            }
            timeoutId = setTimeout(run, delay);
        };

        const unsub = store.subscribe((state, prev) => {
            if (state.nextRecomputeAtMs !== prev.nextRecomputeAtMs) {
                schedule();
            }
        });

        run();
        schedule();

        return () => {
            unsub();
            if (timeoutId !== undefined) {
                clearTimeout(timeoutId);
            }
        };
    }, [
        store,
        queriesReady,
        outages,
        formattedOpeningHours,
        outagesDataUpdatedAt,
        openingHoursDataUpdatedAt,
    ]);
}
